import { apiFetch } from './apiClient';

/**
 * Конфиг WebRTC с временными кредами TURN.
 *
 * Пароля TURN в клиенте больше нет: он лежал прямо в бандле, и любой мог гонять
 * наш ретранслятор своим трафиком. Теперь сервер выдаёт на сутки креды,
 * подписанные секретом (схема coturn `use-auth-secret`), — см. GET
 * /api/turn-credentials.
 */

/**
 * Запасной вариант, если за кредами сходить не вышло (сеть отвалилась, сервер
 * ответил ошибкой). Один STUN: звонок соединится напрямую там, где NAT это
 * позволяет, — лучше, чем не звонить вовсе. За симметричным NAT такой звонок не
 * пройдёт, поэтому провал похода за кредами всегда пишем в консоль.
 */
const FALLBACK_ICE_SERVERS = [{ urls: 'stun:turn.aquarium.org.ru:3478' }];

/** На сколько считаем креды протухшими раньше срока — с запасом на долгий разговор. */
const EXPIRY_SKEW_MS = 10 * 60 * 1000;

/** Если сервер не прислал ttl — считаем сутки, как и он. */
const DEFAULT_TTL_SEC = 24 * 60 * 60;

const ICE_CANDIDATE_POOL_SIZE = 10;

/** Последний удачный ответ: { iceServers, expiresAt }. Живёт до перезагрузки вкладки. */
let cached = null;
/** Текущий запрос — чтобы два звонка подряд не ходили за кредами дважды. */
let inFlight = null;

async function fetchIceServers() {
    const response = await apiFetch('/api/turn-credentials');
    if (!response.ok) throw new Error(`turn-credentials: ${response.status}`);

    const data = await response.json();
    if (!Array.isArray(data?.iceServers) || data.iceServers.length === 0) {
        throw new Error('turn-credentials: пустой ответ');
    }

    const ttlSec = Number(data.ttl) > 0 ? Number(data.ttl) : DEFAULT_TTL_SEC;

    return {
        iceServers: data.iceServers,
        expiresAt: Date.now() + ttlSec * 1000 - EXPIRY_SKEW_MS,
    };
}

/**
 * Конфиг для RTCPeerConnection. Ходит на сервер только если кредов нет или они
 * протухли, поэтому звать можно перед каждым звонком.
 *
 * @returns {Promise<{iceServers: Array, iceCandidatePoolSize: number}>}
 */
export async function getRtcConfig() {
    if (cached && cached.expiresAt > Date.now()) {
        return { iceServers: cached.iceServers, iceCandidatePoolSize: ICE_CANDIDATE_POOL_SIZE };
    }

    if (!inFlight) {
        inFlight = fetchIceServers().finally(() => { inFlight = null; });
    }

    try {
        cached = await inFlight;
        return { iceServers: cached.iceServers, iceCandidatePoolSize: ICE_CANDIDATE_POOL_SIZE };
    } catch (error) {
        console.error('Не удалось получить креды TURN:', error);
        // Протухшие креды всё же лучше молчания: TURN отвергнет их сам, а вот
        // свежими они могли и остаться, если сервер просто моргнул.
        const iceServers = cached?.iceServers ?? FALLBACK_ICE_SERVERS;
        return { iceServers, iceCandidatePoolSize: ICE_CANDIDATE_POOL_SIZE };
    }
}

/**
 * Прогрев кэша: креды нужны в момент создания соединения, а к этому времени
 * ответ сервера уже лежит здесь и звонок не ждёт лишний round-trip.
 */
export function prefetchRtcConfig() {
    getRtcConfig().catch(() => { });
}

/** Сброс на выходе из аккаунта: креды выданы конкретному пользователю. */
export function resetTurnCredentials() {
    cached = null;
    inFlight = null;
}
